import { useEffect, useRef, useState } from 'react';
import { getResultCacheWindow } from '../services/resultCacheApi';

const PLAINTEXT_PER_RS_ROW_CAP = 1000;

type Params = {
  enabled: boolean;
  cacheUris: readonly (string | undefined)[] | undefined;
  rowCounts: readonly number[];
  cacheGeneration: number | undefined;
};

export type WindowedPlaintextRows = Record<number, Record<string, unknown>[]>;

export function useWindowedPlaintextRows({ enabled, cacheUris, rowCounts, cacheGeneration }: Params): WindowedPlaintextRows {
  const [rowsByIndex, setRowsByIndex] = useState<WindowedPlaintextRows>({});
  const lastKeyRef = useRef<string>('');

  useEffect(() => {
    if (!enabled || !cacheUris || cacheUris.length === 0) {
      if (lastKeyRef.current !== '') {
        lastKeyRef.current = '';
        setRowsByIndex({});
      }
      return;
    }

    const fetchKey = JSON.stringify({ g: cacheGeneration, u: cacheUris, c: rowCounts });
    if (fetchKey === lastKeyRef.current) return;
    lastKeyRef.current = fetchKey;

    let cancelled = false;
    const next: WindowedPlaintextRows = {};

    void (async () => {
      // Serialized fan-out: parallel `query/subset` calls against the same SSTS
      // ownerUri race and silently return empty rows. Sequential awaits avoid
      // that and keep per-set ordering correct.
      for (let index = 0; index < cacheUris.length; index++) {
        if (cancelled) return;
        const uri = cacheUris[index];
        if (!uri) continue;
        const rowCount = rowCounts[index] ?? 0;
        if (rowCount <= 0) {
          next[index] = [];
          continue;
        }
        try {
          const response = await getResultCacheWindow({
            uri,
            startIndex: 0,
            count: Math.min(PLAINTEXT_PER_RS_ROW_CAP, rowCount),
            expectedGeneration: 0,
          });
          if (cancelled) return;
          next[index] = response.rows;
        } catch (err) {
          if (cancelled) return;
          // Don't silently set [] — that would render as a successful empty
          // fetch in the consumer. Surface the error so it's diagnosable, then
          // leave `next[index]` undefined so the renderer's fallback paths
          // (wdBufferRows, rs.rows) still apply.
          console.error(
            `[useWindowedPlaintextRows] getResultCacheWindow failed for ${uri}:`,
            err,
          );
        }
      }
      if (cancelled) return;
      setRowsByIndex(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, cacheUris, rowCounts, cacheGeneration]);

  return rowsByIndex;
}

export { PLAINTEXT_PER_RS_ROW_CAP };
